import { useCallback, useEffect, useRef, useState } from "react";

const OTP_EXPIRY_KEY = "otp_expiry";

function setStoredExpiryMs(ms) {
  try {
    localStorage.setItem(OTP_EXPIRY_KEY, String(ms));
  } catch {
    /* ignore quota / private mode */
  }
}

function clearStoredExpiry() {
  try {
    localStorage.removeItem(OTP_EXPIRY_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * No key → same as pre-persistence (full `initialSeconds`). Corrupt / expired → 0 and drop key.
 */
function readInitialSecondsLeft() {
  try {
    const raw = localStorage.getItem(OTP_EXPIRY_KEY);
 
    if (raw === null || raw === "") return 0;

    const expiryMs = Number(raw);
    if (!Number.isFinite(expiryMs) || expiryMs <= 0) {
      clearStoredExpiry();
      return 0;
    }

    const remaining = Math.max(
      0,
      Math.floor((expiryMs - Date.now()) / 1000),
    );
    if (remaining === 0) clearStoredExpiry();
    return remaining;
  } catch {
    return 0;
  }
}

/**
 * Resend cooldown. Only ticks while `enabled` is true (e.g. server allows resend).
 * When `enabled` becomes false, pending ticks are cleared — countdown stops immediately.
 * When `enabled` goes from false → true, cooldown restarts at `initialSeconds`.
 *
 * Persists end time in localStorage (`otp_expiry`) so remaining time survives refresh.
 *
 * @param {number} initialSeconds
 * @param {boolean} enabled  When false, timer does not run and `isCooldownOver` stays false.
 */
export const useOtpCountdown = (initialSeconds = 60, enabled = true) => {
  const [secondsLeft, setSecondsLeft] = useState(() =>
    readInitialSecondsLeft(),
  );
  const prevEnabledRef = useRef(enabled);

  useEffect(() => {
    prevEnabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      clearStoredExpiry();
      setSecondsLeft(0);
      return;
    }
  
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearStoredExpiry();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  
    return () => clearInterval(id);
  }, [enabled]);

  const reset = useCallback(() => {
    const expiry = Date.now() + initialSeconds * 1000;
    setStoredExpiryMs(expiry);
    setSecondsLeft(initialSeconds);
  }, [initialSeconds]);

  /** True when server allows countdown and the wait period has finished. */
  const isCooldownOver = enabled && secondsLeft <= 0;

  return { secondsLeft, reset, isCooldownOver };
};
